import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Row, 
  Col, 
  Form, 
  Button, 
  FormCheck,
  Image
} from 'react-bootstrap';
import '../../styles/login.css';
import { useThemeMode } from '../../ThemeContext';
import { authAPI, getDeviceInfo } from '../../helpers/api_service';
import { APP_URLS } from '../../helpers/url_helper';
import sessionManager from '../../helpers/session_manager';
import { clearAllOAuthData, forceMicrosoftReAuth } from '../../helpers/oauth_logout';
import { getFCMToken, isNotificationSupported } from '../../firebase';
import GoogleLogin from './GoogleLogin';
import MicrosoftLogin from './MicrosoftLogin';
import DeviceConflictPopup from './DeviceConflictPopup';
import { MessageAlert } from '../Common';

// Import assets
import Logo from '../../assets/logo.svg';
import EmailIcon from '../../assets/email.svg';
import PasswordIcon from '../../assets/password.svg';
import EyeOpenIcon from '../../assets/eye-open.svg';
import ChevronRight from '../../assets/chevron-right.svg';
import LoginBanner1 from '../../assets/loginban1.png';
import LoginBanner2 from '../../assets/loginban2.png';
import LoginBanner3 from '../../assets/loginban3.png';

// Constants
const loginBanners = [LoginBanner1, LoginBanner2, LoginBanner3];
const FCM_TOKEN_TIMEOUT_MS = 2000;



function Login() {
  // ========================================
  // STATE MANAGEMENT
  // ========================================
  const [showPassword, setShowPassword] = useState(false);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [formData, setFormData] = useState({
    username_or_email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  
  // Hooks
  const location = useLocation();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  
  // State for password reset success message
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  
  // State for device conflict popup
  const [showDeviceConflict, setShowDeviceConflict] = useState(false);
  const [deviceConflictInfo, setDeviceConflictInfo] = useState(null);
  const [pendingLoginData, setPendingLoginData] = useState(null);


  // ========================================
  // EFFECTS
  // ========================================
  
  // Image slider effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % loginBanners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Set body class for legacy CSS compatibility
  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(mode === 'dark' ? 'dark-mode' : 'light-mode');
    return () => {
      document.body.classList.remove('dark-mode', 'light-mode');
    };
  }, [mode]);

  // Clear OAuth data when login page loads to force fresh authentication
  useEffect(() => {
    clearAllOAuthData();
    // Force Microsoft re-authentication specifically
    forceMicrosoftReAuth();
  }, []);

  // Handle password reset success message
  useEffect(() => {
    if (location.state?.passwordResetSuccess) {
      setPasswordResetSuccess(true);
      // Clear the location state to prevent persistence on refresh
      navigate(location.pathname, { replace: true });
      // Auto-hide the message after 5 seconds
      const timer = setTimeout(() => {
        setPasswordResetSuccess(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state?.passwordResetSuccess, navigate, location.pathname]);

  // Handle session expired message from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const message = urlParams.get('message');
    const type = urlParams.get('type');
    
    if (message && type === 'error') {
      setError(decodeURIComponent(message));
      // Clear the URL parameters to prevent persistence on refresh
      navigate(location.pathname, { replace: true });
      // Auto-hide the message after 7 seconds
      const timer = setTimeout(() => {
        setError('');
      }, 7000);
      return () => clearTimeout(timer);
    }
  }, [location.search, navigate, location.pathname]);



  // ========================================
  // FORM HANDLERS
  // ========================================
  
  // Function to check if form has any errors
  const hasFormErrors = () => {
    let errors = {};
    if (!formData.username_or_email) {
      console.log('Username or email is required...');
      errors.username_or_email = true;
    }
    if (!formData.password) {
      console.log('Password is required...');
      errors.password = true;
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    // Clear form validation errors
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // ========================================
  // AUTHENTICATION HANDLERS
  // ========================================
  
  // Helper function to get browser name from device info
  const getBrowserName = () => {
    const deviceInfo = JSON.parse(getDeviceInfo());
    return deviceInfo.browser_name || 'Unknown';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Check for form validation errors first
    if (!hasFormErrors()) {
      console.log('Form validation failed');
      return;
    }

    setIsLoading(true);

    try {
      // Get FCM token if notifications are supported
      let fcmToken = null;
      if (isNotificationSupported()) {
        try {
          // Avoid blocking login for too long if notification/token flow is slow.
          const timeoutPromise = new Promise((resolve) => {
            setTimeout(() => resolve(null), FCM_TOKEN_TIMEOUT_MS);
          });
          fcmToken = await Promise.race([getFCMToken(), timeoutPromise]);
          if (fcmToken) {
            console.log('FCM Token obtained for login');
          } else {
            console.warn(`FCM token not available within ${FCM_TOKEN_TIMEOUT_MS}ms, continuing login without token`);
          }
        } catch (fcmError) {
          console.warn('Failed to get FCM token:', fcmError);
          // Continue with login even if FCM token fails
        }
      }

      const loginPayload = {
        username_or_email: formData.username_or_email,
        password: formData.password,
        device_info: getDeviceInfo(),
        browser_name: getBrowserName(),
        device_type: "web",
        fcm_token: fcmToken
      };
      
      const data = await authAPI.login(loginPayload);

      if (data.status === 'success') {
        // Check for device limit reached
        if (data.data.device_limit_reached === true) {
          // Show device conflict popup
          setDeviceConflictInfo(data.data.active_device_sessions);
          setPendingLoginData(data.data);
          setShowDeviceConflict(true);
          return;
        }
        
        // No device conflict, proceed with normal login
        proceedWithLogin(data.data);
      } else {
        setError(data.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to proceed with login after device conflict is resolved
  const proceedWithLogin = (loginData) => {
    // Start session using session manager
    sessionManager.startSession(loginData.token);
    
    // Save additional user data for topbar
    localStorage.setItem('userData', JSON.stringify(loginData));
    localStorage.setItem('userFullName', loginData.full_name || '');
    localStorage.setItem('userProfilePic', loginData.profile_pic || '');
    
    // Check subscription package
    if (!loginData.subscription_package || loginData.subscription_package.status === 'expired') {
      // Redirect to newSubscription page
      navigate('/newSubscription');
    } else {
      // Navigate to dashboard
      navigate(APP_URLS.DASHBOARD);
    }
  };

  // Device conflict handlers
  const handleDeviceConflictContinue = () => {
    if (pendingLoginData) {
      proceedWithLogin(pendingLoginData);
    }
    setShowDeviceConflict(false);
    setDeviceConflictInfo(null);
    setPendingLoginData(null);
  };

  const handleDeviceConflictCancel = () => {
    setShowDeviceConflict(false);
    setDeviceConflictInfo(null);
    setPendingLoginData(null);
    setIsLoading(false);
  };

  // ========================================
  // RENDER
  // ========================================
  
  return (
    <div className={`login-body ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
      <Container fluid className="login-container">
        <Row className="g-0 min-vh-100">
          {/* Left Side: Image/Overlay */}
          <Col lg={6} className="d-none d-lg-block login-left-bg position-relative">
            <div className="login-slider-img-wrap position-absolute w-100 h-100">
              <Image 
                src={loginBanners[currentBanner]} 
                alt="Login Banner" 
                className="login-slider-img" 
                style={{ filter: 'brightness(1.25)' }}
                fluid
              />
            </div>
            <div className="login-left-overlay"></div>
          </Col>
          
          {/* Right Side: Login Form */}
          <Col xs={12} lg={6} className="d-flex align-items-center justify-content-center login-right-bg position-relative">
            <div className="login-blur-bg-top"></div>
            <div className="login-blur-bg-bottom"></div>
            
            <div className="login-form-wrapper w-100" style={{ maxWidth: '486px' }}>
              <div className="text-center mb-5" style={{ marginTop: '32px' }}>
                <Image 
                  src={Logo} 
                  alt="Complimate" 
                  className="login-logo-main mb-4" 
                  style={{ height: '80px', cursor: 'pointer' }}
                  onClick={() => navigate(APP_URLS.HOME)}
                />
              </div>
              
              <div className="mb-4 text-center">
                <h2 className="login-title" style={{ color: mode === 'dark' ? '#26c3c0' : '#009391' }}>
                  Welcome Back to Complimate
                </h2>
                <div className="login-subtitle">
                  Log in to access your compliance tools, assistant, and dashboard.
                </div>
              </div>
              
              <MessageAlert 
                type="success" 
                show={passwordResetSuccess}
                message="Your password has been updated successfully. Please log in with your new password."
              />
              
              <Form noValidate onSubmit={handleSubmit}>
                <MessageAlert 
                  type="error" 
                  show={!!error}
                  message={error}
                />
                
                <div className="mb-3">
                  <div className="position-relative">
                    <span className="login-icon email-icon">
                      <Image src={EmailIcon} alt="Email Icon" width={20} height={20} />
                    </span>
                    <Form.Control 
                      type="text" 
                      name="username_or_email"
                      value={formData.username_or_email}
                      onChange={handleInputChange}
                      className={`login-input ps-5 ${!formData.username_or_email && formErrors.username_or_email ? 'login-input-error' : ''}`}
                      placeholder="Username or Email" 
                      required
                      title="Please Enter Username or Email"
                    />
                  </div>
                  {!formData.username_or_email && formErrors.username_or_email && (
                    <div className="login-error-label">
                      Please enter your username or email.
                    </div>
                  )}
                </div>
                
                <div className="mb-3">
                  <div className="position-relative">
                    <span className="login-icon password-icon">
                      <Image src={PasswordIcon} alt="Password Icon" width={20} height={20} />
                    </span>
                    <Form.Control 
                      type={showPassword ? "text" : "password"} 
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`login-input ps-5 ${!formData.password && formErrors.password ? 'login-input-error' : ''}`}
                      placeholder="Password" 
                      required
                      title="Please Enter Password"
                    />
                    <span 
                      className="login-icon eye-icon" 
                      onClick={togglePasswordVisibility}
                    >
                      <Image src={EyeOpenIcon} alt="Show Password" width={20} height={20} />
                    </span>
                  </div>
                  {!formData.password && formErrors.password && (
                    <div className="login-error-label">
                      Please enter your password.
                    </div>
                  )}
                </div>
                
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <FormCheck>
                    <FormCheck.Input type="checkbox" id="rememberMe" />
                    <FormCheck.Label className="login-remember-label" htmlFor="rememberMe">
                      Remember me
                    </FormCheck.Label>
                  </FormCheck>
                  <Link to={APP_URLS.FORGOT_PASSWORD} className="login-forgot">Forgot Password?</Link>
                </div>
                
                <Button 
                  type="submit" 
                  className="btn-teal w-100 login-btn d-flex align-items-center justify-content-center"
                  disabled={isLoading}
                >
                  {isLoading ? 'Logging In...' : 'Log In'}
                  <span className="ms-2">
                    <Image src={ChevronRight} alt="chevron" style={{ height: '14px' }} />
                  </span>
                </Button>
              </Form>
              
              <div className="login-divider my-4 d-flex align-items-center">
                <div className="flex-grow-1 login-divider-line"></div>
                <div className="mx-3 login-divider-text">OR Log in with</div>
                <div className="flex-grow-1 login-divider-line"></div>
              </div>
              
              <div className="d-flex gap-3 mb-4">
                <GoogleLogin 
                  onError={setError}
                  onSuccess={() => {}}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  navigate={navigate}
                  onDeviceConflict={(deviceInfo, loginData) => {
                    setDeviceConflictInfo(deviceInfo);
                    setPendingLoginData(loginData);
                    setShowDeviceConflict(true);
                  }}
                />
                <MicrosoftLogin 
                  onError={setError}
                  onSuccess={() => {}}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  navigate={navigate}
                  onDeviceConflict={(deviceInfo, loginData) => {
                    setDeviceConflictInfo(deviceInfo);
                    setPendingLoginData(loginData);
                    setShowDeviceConflict(true);
                  }}
                />
              </div>
              
              <div className="text-center mb-4">
                <span className="login-signup-text">
                  Don't have an account? <Link to={APP_URLS.SIGNUP} className="login-signup-link">Sign up here.</Link>
                </span>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      
      {/* Device Conflict Popup */}
      <DeviceConflictPopup
        show={showDeviceConflict}
        onHide={handleDeviceConflictCancel}
        onContinue={handleDeviceConflictContinue}
        activeDeviceSessions={deviceConflictInfo}
        loginData={pendingLoginData}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        setError={setError}
      />
    </div>
  );
}

export default Login; 